import { Alert, Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

import InputDate from "../../../components/Forms/Element/InputDate";
import InputText from "../../../components/Forms/Element/InputText";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import SegmentedButtons from "../../../components/Forms/Element/SegmentedButtons";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import FormSection from "../../../components/Common/Form/FormSection";
import HintCallout from "../../../components/Common/HintCallout";
import HintTooltip from "../../../components/Common/HintTooltip";
import { parseYYYMMDDToDate } from "../../../../utils/DateUtils";

// Sugestões do campo Motivo; o texto continua livre.
const REASON_SUGGESTIONS = ['Feriado', 'Ponto facultativo', 'Véspera de feriado', 'Evento', 'Manutenção'];

// Mesmos rótulos do horário de funcionamento da filial, que o gestor já conhece, com a dica de cada um.
const TIME_FIELDS = [
    ['openTime', 'open_time', 'Horário em que as filiais começam a atender nesse dia.'],
    ['closeTime', 'close_time', 'Horário em que as filiais fecham. O agendamento precisa terminar até ele.'],
    ['breakStartTime', 'break_start', 'Início de uma pausa, como o almoço. Opcional: deixe vazio se não houver.'],
    ['breakEndTime', 'break_end', 'Fim da pausa. Nenhum agendamento é aceito entre o início e o fim do intervalo.']
];

// Dicas dos campos, no ícone de interrogação ao lado do rótulo.
const HINTS = {
    date: 'Dia em que as filiais saem do horário semanal. Pode ser um dia que já passou, para os relatórios do período ficarem certos.',
    operation: 'Fechada: ninguém consegue agendar nesse dia, nem pela agenda, nem pelo agendamento online, nem pelo WhatsApp. '
        + 'Horário especial: as filiais abrem só na faixa informada, como numa véspera de Natal.',
    reason: 'Aparece para a recepção na agenda, para o cliente no agendamento online e no assistente do WhatsApp. '
        + 'Ex.: Feriado, Ponto facultativo, Manutenção.',
    branches: 'Filiais em que vale esse dia. As que ficarem de fora seguem o horário semanal normalmente.'
};

/** "09:00:00" vira "09:00", que é o que o campo de hora aceita. */
const toInputTime = (time) => (time ? String(time).slice(0, 5) : '');

const Form = (props) => {

    const { t } = useTranslation();
    const { formik, entityData, branchOptions } = props;

    // Cadastro novo nasce com todas as filiais do usuário, assim que a lista chega.
    useEffect(() => {
        if (!entityData && branchOptions.length > 0 && formik.values.branches.length === 0) {
            formik.setFieldValue('branches', branchOptions);
        }
        // eslint-disable-next-line
    }, [branchOptions, entityData]);

    // Preenche os campos na edição.
    useEffect(() => {
        if (entityData) {
            formik.setValues({
                specialDate: parseYYYMMDDToDate(entityData.specialDate),
                isOpen: Boolean(entityData.isOpen),
                openTime: toInputTime(entityData.openTime),
                closeTime: toInputTime(entityData.closeTime),
                breakStartTime: toInputTime(entityData.breakStartTime),
                breakEndTime: toInputTime(entityData.breakEndTime),
                reason: entityData.reason || '',
                branches: (entityData.branchIds || []).map((id, index) => ({
                    value: id,
                    label: entityData.branchNames?.[index] || ''
                }))
            });
        }
        // eslint-disable-next-line
    }, [entityData]);

    const affected = entityData?.affectedAppointments || [];

    return (
        <div className="form-layout form-layout--spaced">
            <FormSection
                title={t('special_day')}
                description="Escolha o dia, se as filiais fecham ou abrem em horário especial e o motivo."
                icon="fa-solid fa-calendar-xmark"
            >
                {/* Na edição, quem já estava marcado no dia aparece antes de tudo: é o que a
                    recepção precisa remarcar ou avisar. */}
                {affected.length > 0 && (
                    <HintCallout tone="warning" icon="fa-triangle-exclamation"
                        title={affected.length === 1 ? '1 agendamento a remarcar' : `${affected.length} agendamentos a remarcar`}>
                        {affected.map((appointment) => `${String(appointment.scheduledDate || '').slice(11, 16)} ${appointment.customerName || 'Cliente'}`
                            + `${appointment.employeeName ? ` com ${appointment.employeeName}` : ''}`
                            + `${appointment.branchName ? ` (${appointment.branchName})` : ''}`).join('; ')}
                    </HintCallout>
                )}

                <Row className="g-3">
                    <Col md={3}>
                        <FormGroup>
                            <FormLabel>{t('date')} <AsteriskRequired /> <HintTooltip text={HINTS.date} /></FormLabel>
                            <InputDate
                                selected={formik.values.specialDate}
                                onChange={(value) => formik.setFieldValue('specialDate', value)}
                                error={formik.errors.specialDate}
                            />
                        </FormGroup>
                    </Col>
                    {/* Seletor padrão do sistema na versão compacta: fica na altura dos campos ao
                        lado. Na altura cheia ele ocupava meia tela para uma escolha de duas opções. */}
                    <Col md={4}>
                        <FormGroup>
                            <FormLabel>{t('special_day_operation')} <AsteriskRequired /> <HintTooltip text={HINTS.operation} /></FormLabel>
                            <SegmentedButtons
                                compact
                                ariaLabel={t('special_day_operation')}
                                value={formik.values.isOpen}
                                onChange={(isOpen) => formik.setFieldValue('isOpen', isOpen)}
                                options={[
                                    { value: false, label: 'Fechada', icon: 'fa-solid fa-store-slash' },
                                    { value: true, label: 'Horário especial', icon: 'fa-solid fa-clock' }
                                ]}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={5}>
                        <FormGroup>
                            <FormLabel>{t('reason')} <AsteriskRequired /> <HintTooltip text={HINTS.reason} /></FormLabel>
                            <InputText
                                name="reason"
                                list="special-day-reasons"
                                maxLength={80}
                                value={formik.values.reason}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.errors.reason}
                            />
                            <datalist id="special-day-reasons">
                                {REASON_SUGGESTIONS.map((reason) => <option key={reason} value={reason} />)}
                            </datalist>
                        </FormGroup>
                    </Col>

                    {/* Horários só no horário especial, como campos comuns do formulário. */}
                    {formik.values.isOpen && TIME_FIELDS.map(([field, label, hint]) => (
                        <Col xs={6} md={3} key={field}>
                            <FormGroup>
                                <FormLabel>
                                    {t(label)} {['openTime', 'closeTime'].includes(field) && <AsteriskRequired />} <HintTooltip text={hint} />
                                </FormLabel>
                                <input
                                    type="time"
                                    className="form-control input-default"
                                    value={formik.values[field]}
                                    onChange={(event) => formik.setFieldValue(field, event.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    ))}

                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>{t('branches')} <AsteriskRequired /> <HintTooltip text={HINTS.branches} /></FormLabel>
                            <CustomSelect
                                isMulti
                                value={formik.values.branches}
                                onChange={(options) => formik.setFieldValue('branches', options || [])}
                                options={branchOptions}
                                placeholder={t('select')}
                                error={formik.errors.branches}
                            />
                            {/* Mesmo alerta azul das telas: o padrão do sistema para orientação. */}
                            <Alert variant="info" className="py-2 mt-2 mb-0">
                                <i className="fa-solid fa-circle-info me-2"></i>
                                Tire da lista a filial que vai abrir normalmente nesse dia.
                            </Alert>
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>
        </div>
    );
};

export default Form;
